import clsx from 'clsx';
import { Icon } from '../icon';
import Link from 'next/link';

export interface BreadcrumbPath {
	icon?: string;
	href: string;
	name: string;
	current: boolean;
	onClick?: () => void;
}

interface BreadcrumbProps {
	paths: BreadcrumbPath[];
	keepThisParam?: string;
	className?: string;
	variant?: 'default' | 'compact';
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
	paths,
	className,
	keepThisParam,
	variant = 'default',
}) => {
	if (variant === 'compact') {
		return (
			<nav aria-label="Breadcrumb" className={clsx('flex min-w-0', className)}>
				<ol
					role="list"
					className="flex w-full min-w-0 items-center gap-2 list-none m-0"
				>
					{paths.map((page, index) => (
						<li
							key={page.name}
							className={clsx('flex items-center gap-2', {
								'min-w-0': page.current,
								'shrink-0': !page.current,
							})}
						>
							{index !== 0 && (
								<span aria-hidden="true" className="text-sm text-gray-35">
									›
								</span>
							)}
							{page.icon && (
								<span className="w-3.5 h-3.5 shrink-0">
									<Icon
										src={page.icon}
										className={clsx(
											'w-3.5 h-3.5',
											page.current ? 'text-black' : 'text-gray-35'
										)}
									/>
								</span>
							)}
							<Link
								href={
									keepThisParam
										? page.href.includes('?')
											? `${page.href}&${keepThisParam}`
											: `${page.href}?${keepThisParam}`
										: page.href
								}
								aria-current={page.current ? 'page' : undefined}
								title={page.current ? page.name : undefined}
								className={clsx(
									'block rounded text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
									page.current
										? 'min-w-0 truncate font-semibold text-black'
										: 'text-gray-35'
								)}
								onClick={page.onClick}
							>
								{page.name}
							</Link>
						</li>
					))}
				</ol>
			</nav>
		);
	}

	return (
		<nav aria-label="Breadcrumb" className={clsx('flex', className)}>
			<ol
				role="list"
				className="flex lg:items-center list-none m-0 w-full max-w-fit truncate"
			>
				{paths.map((page, index) => (
					<li key={page.name}>
						<div className="flex items-center">
							{index !== 0 && index <= paths.length && (
								<span
									className={clsx(
										'text-sm  font-medium lg:font-semibold mx-4',
										page.current ? 'text-black' : 'text-opacity-black'
									)}
								>
									/
								</span>
							)}
							{page.icon && (
								<span className="w-4 h-4 mr-2">
									<Icon
										src={page.icon}
										className={clsx(
											'w-4 h-4',
											page.current ? 'text-black' : 'text-opacity-black'
										)}
									/>
								</span>
							)}
							<Link
								href={
									keepThisParam
										? page.href.includes('?')
											? `${page.href}&${keepThisParam}`
											: `${page.href}?${keepThisParam}`
										: page.href
								}
								aria-current={page.current ? 'page' : undefined}
								className={clsx(
									'text-sm font-semibold',
									page.current ? 'text-black' : 'text-opacity-black'
								)}
								onClick={page.onClick}
							>
								{page.name}
							</Link>
						</div>
					</li>
				))}
			</ol>
		</nav>
	);
};
